import React, { useState } from 'react';
import { Button } from "@mui/material";
import InitiatePayoutDialog from "../InitiatePayoutDialog";
import Busy from "../Busy";
import { requestInitiatePayouts } from "../../views/requests/account-report";
import { getErrorMessage } from "../../util/NetworkErrorUtil";
import { useSnackbar } from "../../context/snackbar-context";
import { Location } from "../../types/Location";

interface InitiatePayoutsProps {
  accountId: string;
  locations: Location[];
}

/**
 * Component for initiating payouts for a supplier account.
 * It displays a button that opens a dialog to select a location and initiate payouts for the selected location.
 * The API selects the transactions itself (charges plus pending refunds) for the given locations;
 * an empty locationIds list means all locations.
 * @param param0
 * @param param0.accountId The ID of the supplier account.
 * @param param0.locations The list of locations associated with the supplier account.
 */
const InitiatePayouts = ({ accountId, locations }: InitiatePayoutsProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const { snackbarShowMessage } = useSnackbar();

  const openDialog = (): void => {
    setShowDialog(true);
  };

  const handleInitiatePayouts = async (locationId: string): Promise<void> => {
    Busy.set(true);
    try {
      await requestInitiatePayouts({
        supplierAccountId: accountId,
        locationIds: locationId === 'All' ? [] : [locationId]
      });
      snackbarShowMessage('Payouts initiated successfully.', 'success');
    } catch (error) {
      snackbarShowMessage(
        getErrorMessage(
          error,
          'Failed to initiate payouts. Please try again or contact SecurSpace if this issue persists.'
        ),
        'error'
      );
    } finally {
      Busy.set(false);
      setShowDialog(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={openDialog}
      >
        Initiate Available Payouts
      </Button>
      <InitiatePayoutDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onConfirm={handleInitiatePayouts}
        locations={locations}
      />
    </>
  );
};

export default InitiatePayouts;
